
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileCheck, ArrowRight, Globe } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { VerifiedCountriesDisplay } from "./VerifiedCountriesDisplay";

interface VerificationStatusCardProps {
  profile: any;
  documents: any[];
  loading: boolean;
  verifiedCountries: string[];
  hasVerifiedDocuments: boolean;
}

export function VerificationStatusCard({
  profile,
  documents,
  loading,
  verifiedCountries,
  hasVerifiedDocuments,
}: VerificationStatusCardProps) {
  const navigate = useNavigate();

  const getStatusMessage = () => {
    if (!profile) return "Loading your verification status...";
    if (
      !profile.id_verification_status ||
      (!loading && documents.length === 0)
    ) {
      return "You haven't submitted any identity documents yet.";
    }
    switch (profile.id_verification_status) {
      case "pending":
        return "Your identity verification is being reviewed. We'll notify you once it's processed.";
      case "verified":
        return "Your identity has been verified. Thank you for completing this process.";
      case "rejected":
        return "Your identity verification was not approved. Please submit new documents.";
      default:
        return "Submit your identification documents to verify your identity.";
    }
  };

  const getStatusColor = () => {
    if (
      !profile?.id_verification_status ||
      (!loading && documents.length === 0)
    ) {
      return "text-muted-foreground";
    }
    switch (profile.id_verification_status) {
      case "pending":
        return "text-amber-600";
      case "verified":
        return "text-green-600";
      case "rejected":
        return "text-red-600";
      default:
        return "text-muted-foreground";
    }
  };

  const handleStartVerification = () => {
    navigate("/verification-process");
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row gap-6 items-center">
          <div className="w-16 h-16 rounded-full bg-nation-purple/10 flex items-center justify-center flex-shrink-0">
            <FileCheck className="h-8 w-8 text-nation-purple" />
          </div>
          <div className="flex-grow">
            <h2 className="text-xl font-bold mb-2">Identity Verification</h2>
            <p className={`mb-4 ${getStatusColor()}`}>{getStatusMessage()}</p>
            <VerifiedCountriesDisplay verifiedCountries={verifiedCountries} />
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleStartVerification}
                variant="purple"
                className="w-full sm:w-auto"
              >
                {profile?.id_verification_status === "rejected"
                  ? "Resubmit Documents"
                  : documents.length > 0
                  ? "View Verification Status"
                  : "Start Verification Process"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              {hasVerifiedDocuments && (
                <Button
                  onClick={handleStartVerification}
                  variant="outline"
                  className="w-full sm:w-auto"
                >
                  <Globe className="mr-2 h-4 w-4" />
                  Verify for Additional Countries
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
